/**
 * 세아제강 계열 DLP 운영보고서(IPT-441) 표 렌더링 유틸.
 * Privacy-i API가 주는 표는 컬럼 구성이 표마다 다르므로 임의 키 레코드로 다룬다.
 */

const SUM_LABEL = '합계';
const FULL_PERCENT = '100%';
const PERCENT_COLUMN_CANDIDATES = ['비중', '비율'];

const parseNumeric = (value: string | undefined): number => {
  if (!value) return 0;
  const n = Number(String(value).replace(/,/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const formatWithCommas = (n: number): string => n.toLocaleString('en-US');

/** 표 렌더러(SteelReportTable)가 합계 행을 다른 음영으로 구분할 수 있게 하는 표시 플래그 */
export type SteelTableRow = { isSummaryRow?: boolean; [column: string]: string | boolean | undefined };

/**
 * SPEC F1 — 지정된 건수 컬럼을 합산한 합계 행을 마지막에 추가한다.
 * 비중/비율 컬럼이 있으면 합계 행에서는 100%로 고정한다(개별 값 재계산은 하지 않음).
 * 합계 행에는 isSummaryRow: true를 붙여 렌더러가 일반 데이터 행과 구분해 음영을 줄 수 있게 한다.
 */
export function addSteelTableSumRow(
  rows: Record<string, string>[],
  options: { sumColumns: string[]; labelColumn: string },
): SteelTableRow[] {
  if (rows.length === 0) return [];

  const sumRow: SteelTableRow = { [options.labelColumn]: SUM_LABEL };
  for (const col of options.sumColumns) {
    const total = rows.reduce((acc, row) => acc + parseNumeric(row[col]), 0);
    sumRow[col] = formatWithCommas(total);
  }
  for (const percentCol of PERCENT_COLUMN_CANDIDATES) {
    if (rows[0][percentCol] !== undefined) {
      sumRow[percentCol] = FULL_PERCENT;
    }
  }
  sumRow.isSummaryRow = true;

  return [...rows, sumRow];
}

/**
 * SPEC F2 — 파일 용량 구간 → "예상 파일 종류" 설명 문구 고정 매핑표.
 * IPT-441 Q4 확정(2026-09-15, 이광일) — Confluence 요구사항정의 첨부 스크린샷
 * ("파일 업로드 4/4")에서 실제 화면에 쓰인 설명 문구를 그대로 가져왔다.
 *
 * ⚠️ 2026-09-16 정정(IPT-622 후속) — 키를 요구사항 목업 기준("100KB ~ 1MB(1024KB)" 등)으로
 * 하드코딩했었는데, 실제 Privacy-i API 파일크기 값은 공백·괄호 없는 짧은 형태였다
 * (cron-worker VM 실측 확인: "100KB 이하"/"100KB~1MB"/"1MB~10MB"/"10MB 초과").
 * "100KB 이하"만 우연히 일치해 그 행만 정상 표시되고 나머지 3개 행은 전부 "확인 필요"
 * 폴백으로 노출되고 있었다(박재은님 스크린샷으로 재확인). 키를 실제 API 값으로 교체한다.
 */
const FILE_SIZE_DESCRIPTION_MAP: Record<string, string> = {
  '100KB 이하': '모바일 최적화 이미지, 알림톡 서식 및 경량 텍스트 파일',
  '100KB~1MB': '일반 캡처 화면, 일반 서식 문서 등',
  '1MB~10MB': '대형 업무용 엑셀/PDC, 고화질 현장 사진 등',
  '10MB 초과': 'CAD 도면 모음, 소스코드 및 대용량 압축본 등',
};

export function mapFileSizeToDescription(fileSizeRange: string): string {
  return FILE_SIZE_DESCRIPTION_MAP[fileSizeRange] ?? '확인 필요';
}

/**
 * 제강 계열사 목록 (IPT-441, IPT-619).
 * 백엔드 `dlp-company-group.helper.ts`의 DLP_STEEL_COMPANIES와 동일 목록 —
 * 프론트는 백엔드 모듈을 import할 수 없어 별도로 유지한다.
 */
export const DLP_STEEL_COMPANIES = ['세아제강지주', '세아제강', '세아씨엠', '동아스틸'] as const;

export function isSteelCompany(company: string | undefined | null): boolean {
  if (!company) return false;
  return (DLP_STEEL_COMPANIES as readonly string[]).includes(company);
}

/**
 * IPT-622 — 부서명이 세아제강 계열사명 그대로 오는 항목은 개인 부서가 아니라
 * 계열사 공용 PC 계정이다(운영 API 실측 확인, 2026-09-16). 화면에는 "공용 PC"로 표시한다.
 */
export function normalizeDepartmentLabel(department: string): string {
  return (DLP_STEEL_COMPANIES as readonly string[]).includes(department) ? '공용 PC' : department;
}

/**
 * IPT-621 — 매체 이동 시간대별 현황 구분값 문구 매핑.
 * 운영 Privacy-i API 실측 결과(2026-09-16) 구분값은 "업무시간"/"평일 업무외"/"주말"
 * 단축형이다(2026-09-15 세션의 "이미 시간 정보 포함" 판단은 Confluence 목업만 보고
 * 한 오판이었음). 요청받은 시간 정보 포함 문구로 변환한다.
 */
const MEDIA_TIME_SLOT_LABELS: Record<string, string> = {
  '업무시간': '업무시간(평일 08시30분~17시30분)',
  '평일 업무외': '평일 업무시간 외 (17시 30분 ~ 08시 30분)',
};

export function mapMediaTimeSlotLabel(value: string): string {
  return MEDIA_TIME_SLOT_LABELS[value] ?? value;
}

/**
 * IPT-441 후속 확인(2026-09-15) — 워터마크 해제 신청 부서 TOP5는 이번달+전월을
 * 나란히 보여줘야 한다. YYYYMM(6자리)에서 1개월 전을 계산한다.
 */
export function computePreviousPeriod(period: string): string {
  const year = parseInt(period.slice(0, 4), 10);
  const month = parseInt(period.slice(4, 6), 10);
  const prevMonth = month === 1 ? 12 : month - 1;
  const prevYear = month === 1 ? year - 1 : year;
  return `${prevYear}${String(prevMonth).padStart(2, '0')}`;
}

export interface PivotedProgramRow {
  program: string;
  totalCount: string;
  /** 항상 5칸 — TOP5 미만이면 나머지는 null(화면에서 "-"로 표시) */
  departments: Array<{ name: string; count: string } | null>;
}

const PIVOT_TOP_N = 5;
const PIVOT_FIXED_KEYS = new Set(['프로그램', '부서', '건수']);

/**
 * IPT-441 후속 확인(2026-09-15) — "허용/차단 프로그램별 부서 TOP5"는 API가
 * 프로그램 하나당 최대 5행(부서별)을 반복해서 주는 flat 배열이지만, 실제 화면은
 * 프로그램별로 그룹핑해 TOP5 부서를 가로로 펼친 피벗 표다. 총건수 컬럼명은
 * "첨부건수"/"차단건수"로 다르므로 고정 키(프로그램/부서/건수)가 아닌 나머지 키를
 * 동적으로 찾는다.
 */
export function pivotProgramDepartmentTop5(rows: Record<string, string>[]): PivotedProgramRow[] {
  const order: string[] = [];
  const grouped = new Map<string, Record<string, string>[]>();
  for (const row of rows) {
    const program = row['프로그램'];
    if (!grouped.has(program)) {
      grouped.set(program, []);
      order.push(program);
    }
    grouped.get(program)!.push(row);
  }

  return order.map((program) => {
    const groupRows = grouped.get(program)!;
    const totalCountKey = Object.keys(groupRows[0]).find((k) => !PIVOT_FIXED_KEYS.has(k));
    const totalCount = totalCountKey ? groupRows[0][totalCountKey] : '';
    const departments: PivotedProgramRow['departments'] = [];
    for (let i = 0; i < PIVOT_TOP_N; i++) {
      const r = groupRows[i];
      departments.push(r ? { name: normalizeDepartmentLabel(r['부서']), count: r['건수'] } : null);
    }
    return { program, totalCount, departments };
  });
}
